/**
 * MembersDirectory — creator's own holder list on ManageClan.
 *
 * Reads `GET /api/clans/mine/members` and lets the creator:
 *   1. See every holder sorted by bits_owned desc
 *   2. Search by name/handle
 *   3. Send a one-off Points gift to any holder inline (🎁 Gift button)
 *
 * The gift call is scoped: only holders of THIS creator's clan can receive,
 * server-side daily caps prevent accidents, and the creator's own points
 * balance funds the transfer atomically.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail, resolveAvatarUrl } from '../api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d <= 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const SORT_OPTIONS = [
  { key: 'bits',       label: 'Top holders' },
  { key: 'joined',     label: 'Newest' },
  { key: 'joined_asc', label: 'OGs first' },
  { key: 'spent',      label: 'Top spenders' },
];

const PAGE_SIZE = 25;

export default function MembersDirectory({ onToast, clanHandle = null }) {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('bits');
  const [giftingUid, setGiftingUid] = useState(null);
  const [giftAmount, setGiftAmount] = useState(50);
  const [giftNote, setGiftNote] = useState('');
  const [giftBusy, setGiftBusy] = useState(false);
  const [recentGifts, setRecentGifts] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [isCreatorView, setIsCreatorView] = useState(!clanHandle);
  // Block feature (Feb 2026) — client-side set of handles the viewer has
  // blocked. Hydrated once on mount from /api/dm/blocks, then locally
  // mutated on every toggle so the tile flips between Block ↔ Unblock
  // without a round-trip. Blocks are ONE-DIRECTIONAL: blocking a user
  // stops them from DM-ing you; you can still DM them.
  const [blockedHandles, setBlockedHandles] = useState(() => new Set());
  const [blockBusy, setBlockBusy] = useState(null); // handle currently being toggled

  // When `clanHandle` is set we hit the public directory endpoint (fan view);
  // when it's null we hit /clans/mine/members (creator view). The creator
  // view still works even for the creator visiting their own Clan Feed —
  // the backend returns is_creator=true and we flip modes automatically.
  const endpoint = clanHandle ? `/clans/${clanHandle}/members-directory` : '/clans/mine/members';

  const reload = useCallback(async (term = '', sort = sortKey) => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: {
          ...(term.trim() ? { search: term.trim() } : {}),
          sort,
          limit: PAGE_SIZE,
          offset: 0,
        },
      });
      setMembers(data?.members || []);
      setTotalCount(Number(data?.total) || 0);
      setHasMore(!!data?.has_more);
      if (typeof data?.is_creator === 'boolean') setIsCreatorView(data.is_creator);
    } catch (e) {
      onToast?.({ msg: formatApiErrorDetail(e) || 'Could not load members.' });
    } finally {
      setLoading(false);
    }
  }, [onToast, sortKey, endpoint]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(endpoint, {
        params: {
          ...(search.trim() ? { search: search.trim() } : {}),
          sort: sortKey,
          limit: PAGE_SIZE,
          offset: members.length,
        },
      });
      setMembers((prev) => [...prev, ...(data?.members || [])]);
      setHasMore(!!data?.has_more);
    } catch (e) {
      onToast?.({ msg: formatApiErrorDetail(e) || 'Could not load more members.' });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, search, sortKey, members.length, onToast, endpoint]);

  const loadRecentGifts = useCallback(async () => {
    // Recent-gifts panel is creator-only (uses /clans/mine/gifts/recent)
    if (clanHandle) return;
    try {
      const { data } = await api.get('/clans/mine/gifts/recent', { params: { limit: 5 } });
      setRecentGifts(data?.gifts || []);
    } catch {
      // Silent — recent gifts is a bonus panel; failure shouldn't spam toasts
    }
  }, [clanHandle]);

  useEffect(() => {
    reload('', sortKey);
    loadRecentGifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey]);

  // Hydrate the block set once on mount so tiles render the right label.
  useEffect(() => {
    api.get('/dm/blocks')
      .then(({ data }) => setBlockedHandles(new Set((data?.blocks || []).map((b) => b.handle))))
      .catch(() => setBlockedHandles(new Set()));
  }, []);

  const toggleBlock = async (handle) => {
    if (!handle || blockBusy === handle) return;
    const isBlocked = blockedHandles.has(handle);
    setBlockBusy(handle);
    try {
      if (isBlocked) {
        await api.delete(`/dm/block/${handle}`);
        setBlockedHandles((prev) => {
          const next = new Set(prev);
          next.delete(handle);
          return next;
        });
        onToast?.({ msg: `Unblocked @${handle}` });
      } else {
        await api.post(`/dm/block/${handle}`);
        setBlockedHandles((prev) => new Set(prev).add(handle));
        onToast?.({ msg: `🚫 Blocked @${handle} — they can't DM you anymore` });
      }
    } catch (err) {
      onToast?.({ msg: formatApiErrorDetail(err) || 'Could not update block status.' });
    } finally {
      setBlockBusy(null);
    }
  };

  // Debounced search — 350ms
  useEffect(() => {
    const t = setTimeout(() => reload(search, sortKey), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalBits = useMemo(
    () => members.reduce((sum, m) => sum + Number(m.bits_owned || 0), 0),
    [members],
  );

  const openGift = (uid) => {
    setGiftingUid(uid);
    setGiftAmount(50);
    setGiftNote('');
  };

  const closeGift = () => {
    if (giftBusy) return;
    setGiftingUid(null);
    setGiftAmount(50);
    setGiftNote('');
  };

  const sendGift = async (m) => {
    const amt = Number(giftAmount);
    if (!(amt >= 1 && amt <= 10_000)) {
      onToast?.({ msg: 'Amount must be between 1 and 10,000 pts.' });
      return;
    }
    setGiftBusy(true);
    try {
      await api.post(`/clans/mine/members/${m.user_id}/gift`, {
        amount: amt, note: giftNote.trim(),
      });
      onToast?.({ msg: `🎁 Sent ${amt} pts to ${m.display_name}` });
      setGiftingUid(null);
      await Promise.all([reload(search, sortKey), loadRecentGifts()]);
    } catch (err) {
      onToast?.({ msg: formatApiErrorDetail(err) || 'Gift failed.' });
    } finally {
      setGiftBusy(false);
    }
  };

  const exportCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Use axios blob response so we can trigger a native download
      const { data, headers } = await api.get('/clans/mine/members/export.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      // Try to lift the filename from the server's Content-Disposition header
      let filename = 'members.csv';
      const cd = headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      if (match) filename = match[1];
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onToast?.({ msg: '⬇ Members CSV downloaded' });
    } catch (err) {
      onToast?.({ msg: formatApiErrorDetail(err) || 'Export failed.' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mc-section-card" data-testid="members-directory">
      <div className="mc-section-head">
        <span className="mc-section-title">👥 Members</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <span
            className="mc-section-badge"
            style={{
              background: 'rgba(0,180,255,0.15)', color: '#00B4FF',
              border: '1px solid rgba(0,180,255,0.35)',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {members.length}/{totalCount} {totalCount === 1 ? 'holder' : 'holders'} · {fmtCompact(totalBits)} Bits
          </span>
          {members.length > 0 && isCreatorView && (
            <button
              type="button"
              className="mc-btn-secondary"
              onClick={exportCsv}
              disabled={exporting}
              data-testid="members-export-csv"
              title="Download all members as CSV"
              style={{ padding: '4px 10px', fontSize: 11 }}
            >
              {exporting ? '…' : '⬇ CSV'}
            </button>
          )}
        </div>
      </div>
      <p className="mc-section-sub">
        {isCreatorView
          ? <>Everyone who holds Bits in your Clan. Send a one-off Points gift to say thanks — <span style={{ opacity: 0.7 }}>daily cap: 20 gifts / 25K pts total.</span></>
          : <>Everyone who holds Bits in this Clan. Tap a member to view their profile.</>}
      </p>

      {/* Sort chips */}
      <div
        className="mc-chip-row"
        role="radiogroup"
        aria-label="Sort members"
        data-testid="members-sort-row"
        style={{ marginBottom: 10 }}
      >
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={sortKey === opt.key}
            className={`mc-chip${sortKey === opt.key ? ' selected' : ''}`}
            onClick={() => setSortKey(opt.key)}
            data-testid={`members-sort-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <input
        type="text"
        className="mc-input"
        placeholder="Search by name or handle…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="members-search"
        style={{ marginBottom: 12 }}
      />

      {loading && members.length === 0 && (
        <p className="mc-empty">Loading members…</p>
      )}

      {!loading && members.length === 0 && (
        <p className="mc-empty" data-testid="members-empty">
          {search.trim()
            ? 'No members match that search.'
            : 'No holders yet. Share your invite link to bring your community in.'}
        </p>
      )}

      {members.length > 0 && (
        <ul className="mc-list" data-testid="members-list" style={{ marginBottom: 4 }}>
          {members.map((m) => {
            const grad = Array.isArray(m.gradient) && m.gradient.length >= 2 ? m.gradient : ['#8B5CF6', '#F2679E'];
            const avatarSrc = m.avatar ? resolveAvatarUrl(m.avatar) : null;
            const isGifting = giftingUid === m.user_id;
            const canLink = !!m.handle;
            return (
              <li key={m.user_id} className="mc-list-row mc-member-row" data-testid={`member-row-${m.user_id}`} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <div className="mc-member-body" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', flexWrap: 'wrap' }}>
                  {/* Avatar + name area → clickable through to public profile */}
                  {canLink ? (
                    <Link
                      to={`/creator/${m.handle}`}
                      data-testid={`member-profile-link-${m.user_id}`}
                      className="mc-member-idbox"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        flex: 1, minWidth: 0,
                        textDecoration: 'none', color: 'inherit',
                        cursor: 'pointer',
                      }}
                      title={`View ${m.display_name}'s profile`}
                    >
                      <div
                        style={{
                          width: 40, height: 40, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          overflow: 'hidden', flexShrink: 0,
                        }}
                        aria-hidden="true"
                      >
                        {avatarSrc
                          ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                          : <span style={{ fontWeight: 800, color: 'rgba(0,0,0,0.55)', fontSize: 14 }}>{m.initials}</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                          <span className="mc-member-name">{m.display_name}</span>
                          {m.verified && <span style={{ color: '#00B4FF', fontSize: 12 }}>✓</span>}
                          {m.badge_count > 0 && (
                            <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 999, background: 'rgba(255,193,7,0.15)', color: '#FFC107', fontWeight: 700 }}>
                              🏅 {m.badge_count}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.65, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <span>@{m.handle}</span>
                          <span>·</span>
                          <span><b>{fmtCompact(m.bits_owned)}</b> Bits</span>
                          <span>·</span>
                          <span>Joined {fmtDate(m.joined_at)}</span>
                          {m.points_spent_in_clan > 0 && (
                            <>
                              <span>·</span>
                              <span title="Points this fan has spent on your perks">
                                💸 {fmtCompact(m.points_spent_in_clan)} pts spent
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          width: 40, height: 40, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          overflow: 'hidden', flexShrink: 0,
                        }}
                      >
                        <span style={{ fontWeight: 800, color: 'rgba(0,0,0,0.55)', fontSize: 14 }}>{m.initials}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, fontWeight: 700 }}>{m.display_name}</div>
                    </div>
                  )}
                  {!isGifting && (
                    <div className="mc-member-actions" style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {canLink && (
                        <button
                          type="button"
                          className="mc-btn-secondary"
                          onClick={() => navigate(`/messages/${m.handle}`)}
                          data-testid={`member-dm-btn-${m.user_id}`}
                          style={{ padding: '6px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
                          title={`Message ${m.display_name}`}
                        >
                          💬 DM
                        </button>
                      )}
                      {canLink && (
                        <button
                          type="button"
                          className={`mc-btn-block${blockedHandles.has(m.handle) ? ' active' : ''}`}
                          onClick={() => toggleBlock(m.handle)}
                          data-testid={`member-block-btn-${m.user_id}`}
                          disabled={blockBusy === m.handle}
                          title={blockedHandles.has(m.handle)
                            ? `Unblock @${m.handle}`
                            : `Block @${m.handle} from DM-ing you`}
                        >
                          🚫 {blockedHandles.has(m.handle) ? 'Unblock' : 'Block'}
                        </button>
                      )}
                      {isCreatorView && (
                        <button
                          type="button"
                          className="mc-btn-secondary"
                          onClick={() => openGift(m.user_id)}
                          data-testid={`member-gift-btn-${m.user_id}`}
                          style={{ padding: '6px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          🎁 Gift
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {isGifting && isCreatorView && (
                  <div
                    style={{
                      marginTop: 12, padding: 12, borderRadius: 10,
                      background: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.25)',
                    }}
                    data-testid={`member-gift-form-${m.user_id}`}
                  >
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        className="mc-input"
                        min={1}
                        max={10000}
                        step={1}
                        placeholder="Amount"
                        value={giftAmount}
                        onChange={(e) => setGiftAmount(e.target.value)}
                        style={{ flex: '0 0 120px' }}
                        data-testid={`member-gift-amount-${m.user_id}`}
                        autoFocus
                      />
                      <input
                        type="text"
                        className="mc-input"
                        placeholder="Note (optional) — e.g. thanks for being early!"
                        value={giftNote}
                        onChange={(e) => setGiftNote(e.target.value.slice(0, 140))}
                        style={{ flex: 1, minWidth: 180 }}
                        data-testid={`member-gift-note-${m.user_id}`}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="mc-btn-secondary"
                        onClick={closeGift}
                        disabled={giftBusy}
                        data-testid={`member-gift-cancel-${m.user_id}`}
                        style={{ padding: '6px 12px', fontSize: 12 }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="mc-btn-primary"
                        onClick={() => sendGift(m)}
                        disabled={giftBusy}
                        data-testid={`member-gift-send-${m.user_id}`}
                        style={{ padding: '6px 14px', fontSize: 12 }}
                      >
                        {giftBusy ? 'Sending…' : `Send ${giftAmount || 0} pts →`}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* "Show more" — appears only if there's another page to load AND
          we're not in the middle of an initial reload. */}
      {hasMore && !loading && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <button
            type="button"
            className="mc-btn-secondary"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="members-show-more"
            style={{ padding: '8px 20px', fontSize: 12 }}
          >
            {loadingMore ? 'Loading…' : `Show ${Math.min(PAGE_SIZE, totalCount - members.length)} more`}
          </button>
        </div>
      )}

      {/* Recent Gifts strip — shows a creator their community-appreciation
          momentum at a glance. Hides entirely if none yet OR if this is
          a fan view of someone else's Clan. */}
      {isCreatorView && recentGifts.length > 0 && (
        <div
          data-testid="recent-gifts"
          style={{ marginTop: 18, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="mc-section-title" style={{ fontSize: 13, marginBottom: 8 }}>
            Recent gifts you sent
          </div>
          <ul className="mc-list" style={{ margin: 0 }}>
            {recentGifts.map((g) => {
              const grad = Array.isArray(g.holder_gradient) && g.holder_gradient.length >= 2
                ? g.holder_gradient : ['#8B5CF6', '#F2679E'];
              const avatarSrc = g.holder_avatar ? resolveAvatarUrl(g.holder_avatar) : null;
              return (
                <li key={g.id} className="mc-list-row" data-testid={`recent-gift-${g.id}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                    <div
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', flexShrink: 0,
                      }}
                    >
                      {avatarSrc
                        ? <img src={avatarSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : <span style={{ fontWeight: 800, color: 'rgba(0,0,0,0.55)', fontSize: 11 }}>{g.holder_initials}</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                      <div>
                        <b>{g.amount.toLocaleString()} pts</b> to <b>{g.holder_display_name}</b>
                        <span style={{ opacity: 0.6, marginLeft: 8, fontSize: 11 }}>{timeAgo(g.created_at)}</span>
                      </div>
                      {g.note && (
                        <div style={{ opacity: 0.65, fontSize: 11.5, marginTop: 2 }}>"{g.note}"</div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
